import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Calendar, MapPin, Ticket, XCircle, CheckCircle2 } from "lucide-react";
import ChangePasswordCard from "@/components/ChangePasswordCard";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-ZA", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

export default function DashboardPage() {
  const { user, refresh } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ name: "", cell_number: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/my-reservations")
      .then(({ data }) => setReservations(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "",
        cell_number: user.cell_number || "",
      });
    }
  }, [user]);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this reservation?")) return;
    try {
      await api.delete(`/reservations/${id}`);
      toast.success("Reservation cancelled");
      load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/profile", profile);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSavingProfile(false); }
  };

  const active = reservations.filter((r) => r.status === "active");
  const cancelled = reservations.filter((r) => r.status === "cancelled");

  return (
    <div className="ser-section" data-testid="dashboard-page">
      <div className="ser-container max-w-5xl">
        <p className="eyebrow mb-3">My account</p>
        <h1 className="font-serif-display text-4xl md:text-5xl mb-3" style={{ color: "var(--ser-primary-dark)" }}>
          Welcome, {user?.name?.split(" ")[0] || "member"}.
        </h1>
        <p className="mb-12" style={{ color: "var(--ser-text-muted)" }}>
          Manage your reservations and profile details.
        </p>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {[
            { icon: Ticket, label: "Active reservations", value: active.length, color: "var(--ser-primary)" },
            { icon: CheckCircle2, label: "Total reservations", value: reservations.length, color: "var(--ser-clay)" },
            { icon: XCircle, label: "Cancelled", value: cancelled.length, color: "var(--ser-text-muted)" },
          ].map(({ icon: Icon, label, value, color }) => (
            <div key={label} className="ser-card p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: color }}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-serif-display text-3xl" style={{ color: "var(--ser-primary-dark)" }}>{value}</p>
                <p className="text-xs" style={{ color: "var(--ser-text-muted)" }}>{label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <h2 className="font-serif-display text-3xl mb-6" style={{ color: "var(--ser-primary-dark)" }}>
              My reservations
            </h2>
            {loading ? (
              <p style={{ color: "var(--ser-text-muted)" }}>Loading…</p>
            ) : reservations.length === 0 ? (
              <div className="ser-card p-10 text-center" data-testid="no-reservations">
                <p className="mb-4" style={{ color: "var(--ser-text-muted)" }}>You don't have any reservations yet.</p>
                <a href="/events" className="btn-ser-primary">Browse events</a>
              </div>
            ) : (
              <div className="space-y-4">
                {reservations.map((r) => (
                  <div key={r.reservation_id} className="ser-card p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                    data-testid={`reservation-${r.reservation_id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-serif-display text-xl" style={{ color: "var(--ser-primary-dark)" }}>
                          {r.event_title}
                        </h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                          r.status === "active" ? "text-white" : ""
                        }`}
                          style={{
                            backgroundColor: r.status === "active" ? "var(--ser-primary)" : "var(--ser-sand)",
                            color: r.status === "active" ? "white" : "var(--ser-text-muted)",
                          }}>
                          {r.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs" style={{ color: "var(--ser-text-muted)" }}>
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(r.event_date)}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{r.event_location}</span>
                      </div>
                    </div>
                    {r.status === "active" && (
                      <button
                        className="btn-ser-outline text-sm"
                        onClick={() => cancel(r.reservation_id)}
                        data-testid={`cancel-res-${r.reservation_id}`}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="font-serif-display text-3xl mb-6" style={{ color: "var(--ser-primary-dark)" }}>
              Profile
            </h2>
            <form onSubmit={saveProfile} className="ser-card p-6 space-y-4" data-testid="profile-form">
              <div>
                <label className="ser-label">Email</label>
                <input disabled className="ser-input" value={user?.email || ""} />
              </div>
              <div>
                <label className="ser-label">Name</label>
                <input className="ser-input" value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  data-testid="profile-name" />
              </div>
              <div>
                <label className="ser-label">Cell number</label>
                <input className="ser-input" value={profile.cell_number}
                  onChange={(e) => setProfile({ ...profile, cell_number: e.target.value })}
                  data-testid="profile-cell" />
              </div>
              <button type="submit" disabled={savingProfile} className="btn-ser-primary w-full"
                data-testid="save-profile-btn">
                {savingProfile ? "Saving…" : "Save changes"}
              </button>
            </form>

            <div className="mt-6">
              <ChangePasswordCard />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
